import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronDown, X, LogOut, Shield, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

/**
 * TenantContextIndicator - Global Tenant Context Selector
 * 
 * This component displays the currently active tenant context in the top navigation.
 * - Shows "Platform (Global)" when no tenant is selected (sysadmin default view)
 * - Shows tenant name when a tenant context is active
 * - Allows sysadmins to switch between tenants
 * - Provides clear exit mechanism to return to platform context
 * 
 * IMPORTANT: This is a critical UX element to prevent confusion about which
 * tenant's data the user is viewing/modifying.
 */

const PLATFORM_CONTEXT = {
  id: 'platform',
  name: 'Platform (Global)',
  isPlatform: true
};

const TenantContextIndicator = () => {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [currentContext, setCurrentContext] = useState(PLATFORM_CONTEXT);
  const [isOpen, setIsOpen] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const isSysadmin = hasRole('sysadmin');

  // Load current tenant context from localStorage
  useEffect(() => {
    checkCurrentContext();
    
    // Listen for context changes
    const handleContextChange = (e) => {
      checkCurrentContext();
    };
    
    window.addEventListener('tenantContextChanged', handleContextChange);
    window.addEventListener('storage', handleContextChange);
    
    return () => {
      window.removeEventListener('tenantContextChanged', handleContextChange);
      window.removeEventListener('storage', handleContextChange);
    };
  }, []);

  const checkCurrentContext = useCallback(() => {
    const tenantId = localStorage.getItem('impersonated_tenant_id');
    const tenantName = localStorage.getItem('impersonated_tenant_name');
    
    if (tenantId && tenantName) {
      setCurrentContext({
        id: tenantId,
        name: tenantName,
        isPlatform: false
      });
    } else {
      setCurrentContext(PLATFORM_CONTEXT);
    }
  }, []);

  // Load tenants when dropdown opens (sysadmin only)
  const loadTenants = async () => {
    if (!isSysadmin || tenants.length > 0) return;
    
    setLoading(true);
    try {
      const response = await api.listTenants({ limit: 100 });
      // TENANT ISOLATION: Filter out platform tenants from the selector
      const externalTenants = (response.data.tenants || []).filter(
        tenant => !tenant.is_platform_tenant
      );
      setTenants(externalTenants);
    } catch (err) {
      console.error('Failed to load tenants:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDropdown = () => {
    if (isSysadmin) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        loadTenants();
      }
    }
  };

  const handleSelectTenant = (tenant) => {
    localStorage.setItem('impersonated_tenant_id', tenant.id || tenant._id);
    localStorage.setItem('impersonated_tenant_name', tenant.name);
    
    setCurrentContext({
      id: tenant.id || tenant._id,
      name: tenant.name,
      isPlatform: false
    });
    
    window.dispatchEvent(new CustomEvent('tenantContextChanged', { 
      detail: { tenantId: tenant.id || tenant._id, tenantName: tenant.name }
    }));
    
    toast.success(`Entered tenant context: ${tenant.name}`, {
      description: 'You are now viewing data as this tenant.'
    });
    
    setIsOpen(false);
    setSearchQuery('');
    navigate('/dashboard');
  };

  const handleExitTenantContext = (e) => {
    e?.stopPropagation();
    
    localStorage.removeItem('impersonated_tenant_id');
    localStorage.removeItem('impersonated_tenant_name');
    localStorage.removeItem('impersonated_user_id');
    localStorage.removeItem('impersonated_user_email');
    
    setCurrentContext(PLATFORM_CONTEXT);
    
    window.dispatchEvent(new CustomEvent('tenantContextChanged', { 
      detail: { tenantId: null, tenantName: null }
    }));
    
    toast.success('Exited tenant context', {
      description: 'Returned to platform global view.'
    });
    
    setIsOpen(false);
    navigate('/platform/tenants');
  };

  const filteredTenants = tenants.filter(t => 
    t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.slug?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Non-sysadmin users just see their tenant (read-only)
  if (!isSysadmin) {
    const userTenantName = user?.tenant?.name || user?.tenant_name || 'Your Tenant';
    return (
      <div 
        className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg"
        data-testid="tenant-context-readonly"
      >
        <Building2 className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700 max-w-[160px] truncate">
          {userTenantName}
        </span>
      </div>
    );
  }

  return (
    <div className="relative" data-testid="tenant-context-indicator">
      {/* Main Button and Exit Button Container */}
      <div className="flex items-center">
        <button
          onClick={handleOpenDropdown}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors ${
            currentContext.isPlatform
              ? 'bg-slate-100 border-slate-300 hover:bg-slate-200'
              : 'bg-amber-50 border-amber-300 hover:bg-amber-100'
          } ${!currentContext.isPlatform ? 'rounded-r-none border-r-0' : ''}`}
          data-testid="tenant-context-button"
        >
          {currentContext.isPlatform ? (
            <Shield className="w-4 h-4 text-slate-600" />
          ) : (
            <Building2 className="w-4 h-4 text-amber-600" />
          )}
          <span className={`text-sm font-medium max-w-[160px] truncate ${
            currentContext.isPlatform ? 'text-slate-700' : 'text-amber-800'
          }`}>
            {currentContext.name}
          </span>
          <ChevronDown className={`w-4 h-4 transition-transform ${
            isOpen ? 'rotate-180' : ''
          } ${currentContext.isPlatform ? 'text-slate-500' : 'text-amber-600'}`} />
        </button>
        
        {/* Exit button when in tenant context - separate from main button */}
        {!currentContext.isPlatform && (
          <button
            onClick={handleExitTenantContext}
            className="px-2 py-1.5 bg-amber-50 border border-amber-300 border-l-0 rounded-r-lg hover:bg-amber-200 transition-colors"
            title="Exit tenant context"
            data-testid="exit-tenant-context"
          >
            <X className="w-3.5 h-3.5 text-amber-700" />
          </button>
        )}
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40"
            onClick={() => { setIsOpen(false); setSearchQuery(''); }}
          />
          
          {/* Dropdown */}
          <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
            {/* Header */}
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Select Tenant Context
              </p>
            </div>
            
            {/* Warning when in tenant context */}
            {!currentContext.isPlatform && (
              <div className="px-3 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  Currently viewing as: <strong>{currentContext.name}</strong>
                </p>
              </div>
            )}
            
            {/* Platform Option */}
            <button
              onClick={handleExitTenantContext}
              className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors ${
                currentContext.isPlatform ? 'bg-slate-50' : ''
              }`}
              data-testid="select-platform-context"
            >
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                <Shield className="w-4 h-4 text-slate-600" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-gray-900">Platform (Global)</p>
                <p className="text-xs text-gray-500">Admin-level view across all tenants</p>
              </div>
              {currentContext.isPlatform && (
                <span className="text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                  Active
                </span>
              )}
            </button>
            
            <div className="border-t border-gray-100" />
            
            {/* Search */}
            <div className="px-3 py-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tenants..."
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                data-testid="tenant-search-input"
              />
            </div>
            
            {/* Tenant List */}
            <div className="max-h-64 overflow-y-auto">
              {loading ? (
                <div className="px-3 py-4 text-center text-sm text-gray-500">
                  Loading tenants...
                </div>
              ) : filteredTenants.length === 0 ? (
                <div className="px-3 py-4 text-center text-sm text-gray-500">
                  {searchQuery ? 'No tenants match your search' : 'No tenants available'}
                </div>
              ) : (
                filteredTenants.map(tenant => (
                  <button
                    key={tenant.id || tenant._id}
                    onClick={() => handleSelectTenant(tenant)}
                    className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors ${
                      currentContext.id === (tenant.id || tenant._id) ? 'bg-amber-50' : ''
                    }`}
                    data-testid={`select-tenant-${tenant.id || tenant._id}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-indigo-600" />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{tenant.name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {tenant.slug || (tenant._id || tenant.id)?.slice(-8)}
                      </p>
                    </div>
                    {currentContext.id === (tenant.id || tenant._id) && (
                      <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded flex-shrink-0">
                        Active
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
            
            {/* Footer */}
            <div className="px-3 py-2 bg-gray-50 border-t border-gray-100">
              <button
                onClick={() => { navigate('/platform/tenants'); setIsOpen(false); }}
                className="w-full text-center text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                data-testid="go-to-tenant-directory"
              >
                Go to Tenant Directory →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TenantContextIndicator;
